import React, { useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { ASSETS } from "../i18n/translations";
import { ArrowUpRight, ArrowRight, Play, Pause, Volume2, VolumeX } from "lucide-react";

export default function Hero() {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
      setPlaying(false);
    } else {
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 md:pt-36 pb-16 md:pb-20 overflow-hidden">
      <div className="absolute inset-0 tech-grid opacity-60 pointer-events-none" />

      <div className="relative px-6 md:px-10 lg:px-16">
        {/* Top meta row */}
        <div className="flex items-center justify-between mb-8 md:mb-12 flex-wrap gap-y-3">
          <div data-testid="hero-eyebrow" className="font-mono-tech text-[#1d428a]">
            <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
            {t.hero.eyebrow}
          </div>
          <div className="hidden md:flex items-center gap-6 font-mono-tech text-[#5A6270]">
            <span>47.677°N · 8.632°E</span>
            <span>SCHWEIZER PLANMANUFAKTUR GMBH</span>
          </div>
        </div>

        {/* Headline */}
        <div className="grid grid-cols-12 gap-y-8">
          <div className="col-span-12 lg:col-span-8">
            <h1
              data-testid="hero-headline"
              className="font-display text-[#0A0A0A] text-6xl md:text-7xl lg:text-8xl xl:text-[7rem] leading-[0.95]"
            >
              <span className="block text-[#1d428a]">{t.hero.title_1}</span>
              <span className="block">{t.hero.title_2}</span>
              <span className="relative inline-block text-[#df4661]">
                {t.hero.title_3}
                <span className="absolute -bottom-1 left-0 right-0 h-[5px] bg-[#df4661]/30" />
              </span>
            </h1>
          </div>

          <div className="col-span-12 lg:col-span-4 lg:pl-8 flex flex-col justify-end">
            <p data-testid="hero-subtitle" className="text-[#0A0A0A] text-sm md:text-base leading-relaxed max-w-md">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-[#1d428a] text-white font-mono-tech px-5 py-3.5 hover:bg-[#df4661] transition-colors"
              >
                {t.hero.cta_primary}
                <ArrowUpRight size={15} strokeWidth={1.8} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-3 border border-[#1d428a] text-[#1d428a] font-mono-tech px-5 py-3.5 hover:bg-[#1d428a] hover:text-white transition-colors"
              >
                {t.hero.cta_secondary}
                <ArrowRight size={15} strokeWidth={1.8} className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>

        {/* Promo Video */}
        <div className="mt-12 md:mt-16 relative">
          <div className="absolute -top-3 -left-3 font-mono-tech text-[#1d428a]">FIG. 01 / ATELIER · WERKSTATT · MONTAGE</div>
          <div className="relative aspect-[16/9] w-full overflow-hidden border border-[#1d428a]/30 bg-[#0A0A0A]">
            <video
              ref={videoRef}
              data-testid="hero-promo-video"
              src={ASSETS.promoVideo}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/10" />
            <div className="absolute top-4 right-4 flex gap-2">
              <button
                type="button"
                onClick={toggleMute}
                data-testid="hero-video-mute"
                className="w-10 h-10 grid place-items-center bg-black/40 backdrop-blur text-white border border-white/30 hover:bg-[#df4661] hover:border-[#df4661] transition-colors"
                aria-label="Toggle mute"
              >
                {muted ? <VolumeX size={16} strokeWidth={1.6} /> : <Volume2 size={16} strokeWidth={1.6} />}
              </button>
              <button
                type="button"
                onClick={togglePlay}
                data-testid="hero-video-play"
                className="w-10 h-10 grid place-items-center bg-black/40 backdrop-blur text-white border border-white/30 hover:bg-[#df4661] hover:border-[#df4661] transition-colors"
                aria-label="Toggle play"
              >
                {playing ? <Pause size={16} strokeWidth={1.6} /> : <Play size={16} strokeWidth={1.6} />}
              </button>
            </div>
            <div className="absolute bottom-4 left-4 font-mono-tech text-white/80 bg-black/40 backdrop-blur px-2.5 py-1">
              · MONTIEREN · BAUEN · WARTEN
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-16 md:mt-20 border-t border-b border-[#1d428a]/20 grid grid-cols-2 md:grid-cols-4">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              data-testid={`hero-stat-${n}`}
              className={`shift-group group py-6 md:py-8 px-4 md:px-6 ${
                n !== 4 ? "border-r border-[#1d428a]/20" : ""
              } ${n <= 2 ? "border-b md:border-b-0 border-[#1d428a]/20" : ""}`}
            >
              <div className="font-mono-tech text-[#df4661]">0{n}</div>
              <div className="font-display text-3xl md:text-4xl mt-2 shift-up text-[#1d428a]">
                {t.hero[`stat_${n}_value`]}
              </div>
              <div className="font-mono-tech text-[#5A6270] mt-2">{t.hero[`stat_${n}_label`]}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
